import React, { useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, SkipBack, SkipForward, X, Gauge, History } from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────────
   Bottom-center replay player. When a past execution is loaded for
   replay, this bar shows controls (play/pause/step/speed/scrubber)
   plus the current step's node type + reason. Parent owns the state.
   ───────────────────────────────────────────────────────────────────── */

const STATUS_COLOR = {
  ok: 'text-emerald-300',
  matched: 'text-emerald-300',
  prompt: 'text-sky-300',
  repeat: 'text-sky-300',
  main_menu: 'text-sky-300',
  timeout: 'text-amber-300',
  fallback: 'text-amber-300',
  no_answer: 'text-amber-300',
  skipped: 'text-gray-400',
  error: 'text-rose-300',
};

const SPEED_OPTIONS = [0.5, 1, 2, 4];

export default function ReplayPlayer({
  execution,
  stepIndex,
  playing,
  speed,
  onTogglePlay,
  onStepBack,
  onStepForward,
  onSeek,
  onSpeedChange,
  onClose,
}) {
  const steps = useMemo(() => (execution?.steps || []).filter((s) => s.node_id), [execution]);
  const total = steps.length;
  const cur = steps[stepIndex];
  const atEnd = stepIndex >= total - 1;
  const atStart = stepIndex <= 0;

  // Auto-advance when playing
  const timerRef = useRef(null);
  useEffect(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (!playing || atEnd) return;
    const delay = Math.max(120, 1200 / (speed || 1));
    timerRef.current = setTimeout(() => onStepForward(), delay);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [playing, stepIndex, speed, atEnd, onStepForward]);

  if (!execution) return null;

  const fmtTs = (iso) => {
    try { return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
    catch { return ''; }
  };

  return (
    <motion.div
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 80, opacity: 0 }}
      transition={{ type: 'spring', damping: 28, stiffness: 320 }}
      className="absolute bottom-5 left-1/2 -translate-x-1/2 w-[640px] max-w-[92vw] bg-[#0c0d12]/95 backdrop-blur border border-teal-400/30 rounded-2xl shadow-2xl shadow-teal-500/10 overflow-hidden z-40"
      data-testid="replay-player"
    >
      {/* Top row — exec metadata + close */}
      <div className="flex items-center gap-3 px-4 pt-3">
        <div className="w-7 h-7 rounded-lg bg-teal-400/15 border border-teal-400/30 flex items-center justify-center">
          <History size={13} className="text-teal-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-gray-400 leading-none">
            Replaying call from <span className="text-white font-mono">{execution.from_number || 'Unknown'}</span>
            <span className="text-gray-600 mx-1.5">·</span>
            <span className="text-gray-300">{fmtTs(execution.started_at)}</span>
          </div>
          <div className="text-xs font-bold text-teal-300 uppercase tracking-wider mt-0.5">
            {cur?.type || '—'}
            <span className={`ml-2 text-[10px] ${STATUS_COLOR[cur?.status] || 'text-gray-400'}`}>{cur?.status || ''}</span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white p-1 rounded hover:bg-white/5"
          data-testid="replay-close"
        >
          <X size={14} />
        </button>
      </div>

      {/* Reason line */}
      {cur?.reason && (
        <div className="px-4 pt-1 pb-2 text-[11px] text-gray-300 truncate" title={cur.reason} data-testid="replay-reason">
          {cur.reason}
        </div>
      )}

      {/* Scrubber */}
      <div className="px-4 pb-1">
        <input
          type="range"
          min={0}
          max={Math.max(0, total - 1)}
          value={stepIndex}
          onChange={(e) => onSeek(parseInt(e.target.value, 10))}
          className="voxinity-replay-scrubber w-full"
          data-testid="replay-scrubber"
        />
        <div className="flex justify-between text-[9px] text-gray-500 mt-0.5">
          <span>Step {stepIndex + 1} of {total}</span>
          <span>{cur ? fmtTs(cur.ts) : ''}</span>
        </div>
      </div>

      {/* Transport controls */}
      <div className="flex items-center justify-between px-4 pb-3 pt-1">
        <div className="flex items-center gap-1">
          <button
            onClick={onStepBack}
            disabled={atStart}
            className="p-1.5 rounded-md text-gray-300 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
            title="Previous step"
            data-testid="replay-prev"
          >
            <SkipBack size={14} />
          </button>
          <button
            onClick={onTogglePlay}
            disabled={total === 0}
            className="p-2 rounded-lg bg-teal-400 text-[#08090d] hover:bg-teal-300 disabled:opacity-30"
            title={playing ? 'Pause' : 'Play'}
            data-testid="replay-play"
          >
            {playing ? <Pause size={14} /> : <Play size={14} />}
          </button>
          <button
            onClick={onStepForward}
            disabled={atEnd}
            className="p-1.5 rounded-md text-gray-300 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
            title="Next step"
            data-testid="replay-next"
          >
            <SkipForward size={14} />
          </button>
        </div>

        {/* Speed selector */}
        <div className="flex items-center gap-1.5 text-[10px] text-gray-400" data-testid="replay-speed">
          <Gauge size={11} className="text-gray-500" />
          {SPEED_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => onSpeedChange(s)}
              className={`px-2 py-0.5 rounded-md border text-[10px] font-bold ${
                speed === s
                  ? 'bg-teal-400/20 border-teal-400/40 text-teal-200'
                  : 'border-white/10 text-gray-400 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`replay-speed-${s}x`}
            >
              {s}×
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
